var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSpring, Transition, config } from '@react-spring/web';
import cx from 'classnames';
import { useTheme } from '../theme/theme-context';
import { useDropdown } from '../dropdown/dropdown-context';
import Divider from '../divider';
import { getRect } from '../tooltip/utils';
import { defaultValue, MenuContext, useMenu } from './menu-context';
import { Container, Item } from './styles';
import { getActiveTheme } from './utils';
export const SubMenu = (props) => {
    return (React.createElement(React.Fragment, null,
        React.createElement("p", { "data-role": "menu-subtitle" }, props.title),
        React.createElement("ul", { "data-role": "menu-submenu" }, props.children),
        React.createElement(Divider, { type: "horizontal" })));
};
export const MenuItem = React.forwardRef((_a, ref) => {
    var { itemKey, onClick } = _a, props = __rest(_a, ["itemKey", "onClick"]);
    const { theme, type } = useTheme();
    const { selectedKeys, size, handleSelect, menuTheme = 'primary' } = useMenu();
    const isSelected = selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.includes(itemKey);
    const activeTheme = getActiveTheme(theme, menuTheme);
    const [bgStyles, api] = useSpring(() => (Object.assign(Object.assign({}, activeTheme.default), { config: config.stiff })));
    useEffect(() => {
        if (!isSelected) {
            api.start(activeTheme.unSelected);
            return;
        }
        api.start(activeTheme.selected);
    }, [isSelected, type, api, activeTheme]);
    const handleClick = useCallback(() => {
        if (itemKey) {
            handleSelect === null || handleSelect === void 0 ? void 0 : handleSelect(itemKey);
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(itemKey);
    }, [itemKey, handleSelect, onClick]);
    const hasAction = 'action' in props;
    return (React.createElement(Transition, { items: hasAction ? props.action : 'insert', from: { opacity: 0 }, enter: { opacity: 1 }, leave: { opacity: 0 }, 
        // FIXME: height 100% has runtime error
        // from={{ height: 0, opacity: 0 }}
        // enter={{ height: '100%', opacity: 1 }}
        // leave={{ height: 0, opacity: 0 }}
        delay: 200 }, (styles, item) => item && (React.createElement(Item, Object.assign({ ref: ref, "aria-selected": isSelected, "data-role": "menu-item" }, props, { onClick: handleClick, onMouseEnter: () => {
            !isSelected && api.start(activeTheme.hover);
        }, onMouseLeave: () => {
            !isSelected && api.start(activeTheme.unHover);
        }, style: Object.assign(Object.assign({}, styles), bgStyles), size: size, className: cx('menu-item', props.className) }), props.children))));
});
MenuItem.displayName = 'MenuItem';
export const Menu = (props) => {
    const { switchKey, mode } = useDropdown();
    const [selectedKeys, setSelectedKeys] = useState([]);
    const isSwitchMode = mode === 'switch';
    // FIXME: auto -> undefined, fix spring runtime error
    const [styles, api] = useSpring(() => ({
        height: isSwitchMode ? 0 : undefined,
        opacity: isSwitchMode ? 0 : 1,
    }));
    const parent = useRef(null);
    const prev = useRef();
    useEffect(() => {
        if (!isSwitchMode) {
            return;
        }
        if (prev.current && switchKey !== prev.current) {
            const { height } = getRect(parent);
            api.start({ height });
            prev.current = switchKey;
        }
        if (!prev.current) {
            const { height } = getRect(parent);
            api.start({ height, opacity: 1 });
            prev.current = switchKey;
        }
    }, [switchKey, prev, parent, api, isSwitchMode]);
    const [subMenuPopupVisible, setSubMenuPopupVisible] = useState(defaultValue.subMenuPopupVisible);
    const handleSubMenuPopupVisible = useCallback((value) => {
        setSubMenuPopupVisible(value);
    }, []);
    const handleSelect = useCallback((itemKey) => {
        setSelectedKeys([itemKey]);
    }, []);
    return (React.createElement(MenuContext.Provider, { value: {
            subMenuPopupVisible,
            selectedKeys: props.selectedKeys || selectedKeys || [],
            size: props.size || defaultValue.size,
            handleSubMenuPopupVisible,
            handleSelect,
            menuTheme: props.menuTheme,
        } },
        React.createElement(Container, { "data-role": "menu", style: Object.assign(Object.assign({}, styles), props.style), className: cx('menu', props.className), onClick: props.onClick },
            React.createElement("div", { ref: parent, className: "menu-inner", "data-role": "menu-inner" }, props.children))));
};
